import { ActionModalProps } from "@/shared/base/components/ActionModal/ActionModal";
import {
  ModalActionButton,
  ModalActionButtonProps,
} from "@/shared/base/components/ModalActionButton/ModalActionButton";
import { ModalTitleText } from "@/shared/base/components/ModalReusables/ModalReusables";
import {
  Box,
  Dialog,
  DialogProps,
  Divider,
  IconButton,
  Paper,
  Stack,
  SxProps,
  useTheme,
} from "@mui/material";
import React from "react";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import HighlightOffRoundedIcon from "@mui/icons-material/HighlightOffRounded";
import CheckCircleOutlineRoundedIcon from "@mui/icons-material/CheckCircleOutlineRounded";
import { useCssMediaQuery } from "@/shared/base/hooks/useCssMediaQuery";
import { MdOutlineClose } from "react-icons/md";

interface ActionButtonProps {
  variant?: ModalActionButtonProps["variant"];
  label?: string | React.ReactNode;
  isLoading?: boolean;
  isVisible?: boolean;
  isDisabled?: boolean;
  onClick?: () => void;
  style?: React.CSSProperties;
}

interface CloseIconProps {
  isHidden?: boolean;
  isLoading?: boolean;
  isDisabled?: boolean;
  onClick?: () => void;
  style?: React.CSSProperties;
}

export interface NotificationModalProps {
  width?: ActionModalProps["width"];
  isOpen: boolean;
  title?: string | React.ReactNode;
  description?: string | React.ReactNode;
  hideTitleIcon?: boolean;
  hideCloseIcon?: boolean;
  titleIconVariant?: "info" | "warning" | "error" | "success";

  hidePrimaryButton?: boolean;
  hideSecondaryButton?: boolean;
  primaryButtonText?: string | React.ReactNode;
  secondaryButtonText?: string | React.ReactNode;
  onClose?: () => void;
  onClickPrimaryButton?: () => void;
  onClickSecondaryButton?: () => void;
  primaryButtonStyle?: React.CSSProperties;
  secondaryButtonStyle?: React.CSSProperties;
  isPrimaryButtonLoading?: boolean;
  isSecondaryButtonLoading?: boolean;

  // render props
  renderHeader?: () => React.ReactNode;
  renderFooter?: () => React.ReactNode;
  renderBody?: () => React.ReactNode;

  // combined props for both primary and secondary buttons
  secondaryButtonProps?: ActionButtonProps;
  primaryButtonProps?: ActionButtonProps;
  // close icon props
  closeIconProps?: CloseIconProps;
  // modal body props
  bodyProps?: {
    hideTopDivider?: boolean;
    hideBottomDivider?: boolean;
    style?: SxProps;
  };
  // props for footer
  footerProps?: {
    style?: React.CSSProperties;
  };
  // props for paper component
  paperProps?: {
    style?: React.CSSProperties;
  };
  dialogProps?: {
    fullScreen?: boolean;
    maxWidth?: DialogProps["maxWidth"];
  };
}

function calcModalWidth(width: DialogProps["maxWidth"]) {
  const defaultWidth = "700px";
  switch (width) {
    case "xs":
      return "600px";
    case "sm":
      return defaultWidth;
    case "md":
      return "900px";
    case "lg":
      return "1200px";
    case "xl":
      return "1486px";
    default:
      return defaultWidth;
  }
}

const renderTitleIcon = (
  variant: NotificationModalProps["titleIconVariant"]
) => {
  switch (variant) {
    case "info":
      return <InfoOutlinedIcon />;
    case "warning":
      return <WarningAmberRoundedIcon />;
    case "error":
      return <HighlightOffRoundedIcon />;
    case "success":
      return <CheckCircleOutlineRoundedIcon />;
    default:
      return <InfoOutlinedIcon />;
  }
};

export function NotificationModal(props: NotificationModalProps) {
  const theme = useTheme();
  const { palette } = theme;
  const { sm: smDown } = useCssMediaQuery("down");

  return (
    <Dialog
      open={props.isOpen}
      PaperComponent={Paper}
      fullScreen={props?.dialogProps?.fullScreen}
      maxWidth={props?.dialogProps?.maxWidth}
      PaperProps={{
        sx: {
          position: "relative",
          backgroundColor: palette.background.default,
          backgroundImage: "unset",
          border: `1px solid ${palette.divider}`,
          margin: smDown ? "0px" : "initial",
          maxWidth: "100%",
          width: calcModalWidth(props.width),
          ...props?.paperProps?.style,
        },
      }}
      style={{
        backdropFilter: "blur(4px)",
      }}
    >
      {!props.hideCloseIcon && !props?.closeIconProps?.isHidden ? (
        <Box
          sx={{
            position: "absolute",
            top: "12px",
            right: "12px",
            zIndex: 3,
          }}
        >
          <IconButton
            size="medium"
            disabled={props?.closeIconProps?.isDisabled}
            onClick={props?.closeIconProps?.onClick || props?.onClose}
            sx={props?.closeIconProps?.style || null}
          >
            <MdOutlineClose />
          </IconButton>
        </Box>
      ) : null}

      {props?.renderHeader?.() || (
        <Stack
          py={2}
          px={3}
          direction={"row"}
          alignItems={"center"}
          spacing={1.2}
        >
          {!props?.hideTitleIcon
            ? renderTitleIcon(props.titleIconVariant)
            : null}
          <ModalTitleText>{props?.title}</ModalTitleText>
        </Stack>
      )}

      {!props?.bodyProps?.hideTopDivider ? <Divider /> : null}
      <Box
        sx={{
          maxHeight: "750px",
          overflowY: "auto",
          ...props?.bodyProps?.style,
        }}
      >
        {props?.renderBody?.() || <Box p={3}>{props.description}</Box>}
      </Box>

      {!props?.bodyProps?.hideBottomDivider ? <Divider /> : null}
      {props?.renderFooter ? (
        props.renderFooter()
      ) : (
        <Stack
          direction={"row"}
          justifyContent={"flex-end"}
          py={2}
          px={3}
          rowGap={2}
          columnGap={2}
          flexWrap={"wrap"}
          sx={props?.footerProps?.style || null}
        >
          {!props.hideSecondaryButton ||
          props?.secondaryButtonProps?.isVisible ? (
            <ModalActionButton
              variant={props?.secondaryButtonProps?.variant || "secondary"}
              onClick={
                props.onClickSecondaryButton ||
                props?.secondaryButtonProps?.onClick
              }
              style={
                props.secondaryButtonStyle || props?.secondaryButtonProps?.style
              }
              isLoading={
                props.isSecondaryButtonLoading ||
                props?.secondaryButtonProps?.isLoading
              }
              disabled={props?.secondaryButtonProps?.isDisabled}
            >
              {props?.secondaryButtonText ||
                props?.secondaryButtonProps?.label ||
                "Close"}
            </ModalActionButton>
          ) : null}
          {!props.hidePrimaryButton || props?.primaryButtonProps?.isVisible ? (
            <ModalActionButton
              variant={props?.primaryButtonProps?.variant || "primary"}
              onClick={
                props.onClickPrimaryButton || props?.primaryButtonProps?.onClick
              }
              style={
                props.primaryButtonStyle || props?.primaryButtonProps?.style
              }
              isLoading={
                props.isPrimaryButtonLoading ||
                props?.primaryButtonProps?.isLoading
              }
              disabled={props?.primaryButtonProps?.isDisabled}
            >
              {props?.primaryButtonText ||
                props?.primaryButtonProps?.label ||
                "Confirm"}
            </ModalActionButton>
          ) : null}
        </Stack>
      )}
    </Dialog>
  );
}
